import { ReactNode, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { LoaderFullscreen } from '@/components/layout/Loader';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import { TabNavigationLayout } from '@/components/generic/nav/TabNavigationLayout';
import { ADMIN_TABS, useAdminTabs } from '../useAdminTabs';
import { AdminHero } from '../AdminHero';
import { PageTitle } from '@/components/nav/PageTitle';
import { Breadcrumb } from '@/components/nav/Breadcrumb';
import {
  useAdminCmsScreen_DetectInvalidSlugsQuery,
  useAdminCmsScreen_DetectMissingStandardSlugsQuery,
} from '@/graphql/generated';
import { SearchIcon } from '@/components/icons';

const TABLES = [
  'indicator',
  'report_disclosure_requirement',
  'dimension',
  'report_data_point',
  'dimension_item',
  'report_standard_requirement',
  'indicatorValue',
] as const;
type TableType = (typeof TABLES)[number];

function TableCard({ table }: { table: TableType }) {
  const { t } = useTranslation();
  const [triggered, setTriggered] = useState(false);
  const [results, setResults] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const query = useAdminCmsScreen_DetectInvalidSlugsQuery({
    variables: { tables: [] },
    skip: true,
  });

  const handleDetectInvalidSlugs = async () => {
    setLoading(true);
    setTriggered(true);
    setError(null);
    try {
      const { data } = await query.refetch({ tables: [table] });
      // Since the response is for a single table, just take the first item
      const found = data?.detectInvalidSlugs?.[0];
      setResults(found?.slugs || []);
    } catch {
      setError(t('admin:cms.unknownError'));
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <div className="flex items-center justify-between gap-4 mb-2">
        <div className="sami-body-bold capitalize">
          {table.replace(/_/g, ' ')}
        </div>
        <button
          onClick={() => void handleDetectInvalidSlugs()}
          disabled={loading}
          className="primary"
        >
          <SearchIcon />
          {t('admin:cms.detectInvalidSlugs')}
        </button>
      </div>
      {loading && <LoaderFullscreen />}
      {error && <MessageBox type={MessageBoxType.Error}>{error}</MessageBox>}
      {triggered &&
        !loading &&
        !error &&
        (results.length > 0 ? (
          <MessageBox type={MessageBoxType.Error}>
            <div className="mb-1">
              {t('admin:cms.invalidSlugsFound', {
                count: results.length,
              })}
            </div>
            <ul className="space-y-1 ml-4">
              {results.map((slug, idx) => (
                <li key={idx} className="list-disc">
                  {slug}
                </li>
              ))}
            </ul>
          </MessageBox>
        ) : (
          <MessageBox type={MessageBoxType.Success}>
            {t('admin:cms.allSlugsValid')}
          </MessageBox>
        ))}
      {!triggered && !loading && !error && (
        <div className="text-gray-500 sami-body-small mt-2">
          {t('admin:cms.clickToScan')}
        </div>
      )}
    </div>
  );
}

function MissingStandardSlugsCard() {
  const { t } = useTranslation();
  const [triggered, setTriggered] = useState(false);
  const [results, setResults] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const query = useAdminCmsScreen_DetectMissingStandardSlugsQuery({
    skip: true,
  });

  const handleDetectMissingStandardSlugs = async () => {
    setLoading(true);
    setTriggered(true);
    setError(null);
    try {
      const { data } = await query.refetch();
      // Type assertion needed until GraphQL codegen runs
      const slugs = (data as { detectMissingStandardSlugs?: string[] })
        ?.detectMissingStandardSlugs;
      setResults(slugs || []);
    } catch {
      setError(t('admin:cms.unknownError'));
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <div className="flex items-center justify-between gap-4 mb-2">
        <div className="sami-body-bold">
          {t('admin:cms.missingStandardSlugs')}
        </div>
        <button
          onClick={() => void handleDetectMissingStandardSlugs()}
          disabled={loading}
          className="primary"
        >
          <SearchIcon />
          {t('admin:cms.detectMissingStandardSlugs')}
        </button>
      </div>
      {loading && <LoaderFullscreen />}
      {error && <MessageBox type={MessageBoxType.Error}>{error}</MessageBox>}
      {triggered &&
        !loading &&
        !error &&
        (results.length > 0 ? (
          <MessageBox type={MessageBoxType.Error}>
            <div className="mb-1">
              {t('admin:cms.missingStandardSlugsFound', {
                count: results.length,
              })}
            </div>
            <ul className="space-y-1 ml-4">
              {results.map((slug: string, idx: number) => (
                <li key={idx} className="list-disc">
                  {slug}
                </li>
              ))}
            </ul>
          </MessageBox>
        ) : (
          <MessageBox type={MessageBoxType.Success}>
            {t('admin:cms.allStandardSlugsMapped')}
          </MessageBox>
        ))}
      {!triggered && !loading && !error && (
        <div className="text-gray-500 sami-body-small mt-2">
          {t('admin:cms.clickToScanStandards')}
        </div>
      )}
    </div>
  );
}

export function AdminCmsScreen() {
  const { t } = useTranslation();
  const adminTabs = useAdminTabs();

  return (
    <>
      <Breadcrumb />
      <TabNavigationLayout<ADMIN_TABS>
        tabs={adminTabs}
        activeTab={ADMIN_TABS.CMS}
        renderTabs={(tabsContent: ReactNode) => (
          <AdminHero>{tabsContent}</AdminHero>
        )}
      >
        <PageTitle title={t('admin:cms.title')} />
        <div className="w-full main-content p-8">
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h1 className="">{t('admin:cms.cmsManagement')}</h1>
              </div>
            </div>

            <div className="bg-white rounded-lg border p-6">
              <h5 className="text-gray-600 mb-6">
                {t('admin:cms.description')}
              </h5>
              <div className="space-y-4">
                <MissingStandardSlugsCard />
                {TABLES.map((table) => (
                  <TableCard key={table} table={table} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </TabNavigationLayout>
    </>
  );
}
